// Tenant change-request numbering config, loaded ONCE at the app shell (Layout)
// and shared via context so every change-management surface can format `#number`
// consistently without each one re-fetching. The raw integer is what the API
// stores; this only carries the display prefix + zero-padding width.
// Mirrors facilityNumbering.tsx. Falls back to the legacy bare-integer format
// until (or if) the config loads.

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { getChangeNumbering } from "./api";
import { DEFAULT_TICKET_NUMBER_FORMAT, type TicketNumberFormat } from "./format";

const ChangeNumberingContext = createContext<TicketNumberFormat>(DEFAULT_TICKET_NUMBER_FORMAT);

export function ChangeNumberingProvider({ children }: { children: ReactNode }) {
  const [cfg, setCfg] = useState<TicketNumberFormat>(DEFAULT_TICKET_NUMBER_FORMAT);

  useEffect(() => {
    const ctrl = new AbortController();
    getChangeNumbering(ctrl.signal)
      .then((res) => setCfg({ prefix: res.numbering.prefix, padding: res.numbering.padding }))
      .catch(() => {
        /* transient failure → keep the safe bare-integer default */
      });
    return () => ctrl.abort();
  }, []);

  return <ChangeNumberingContext.Provider value={cfg}>{children}</ChangeNumberingContext.Provider>;
}

/** The current tenant's change-request-number display config (prefix + padding). */
export function useChangeNumberFormat(): TicketNumberFormat {
  return useContext(ChangeNumberingContext);
}
